import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import {
  Activity,
  ArrowLeft,
  Brain,
  Calendar,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  FileSignature,
  Image as ImageIcon,
  ShieldCheck,
  Smile,
  Stethoscope,
  UserRound
} from "lucide-react";

export default function ProntuariosTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">

      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">

          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link to="/ajuda/tutoriais" className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block">
              Central de Guias
            </Link>
            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>

        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">

        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda">Ajuda</Link>
          <ChevronRight size={14} />
          <Link to="/ajuda/tutoriais">Guias</Link>
          <ChevronRight size={14} />
          <span className="text-indigo-600">Prontuários</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Prontuários dos Pacientes
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>10 min de leitura</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Prontuários
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">

          <p className="lead">
            O prontuário é o centro clínico do OdontoAssist AI, reunindo todas as informações do paciente em um único local.
          </p>

          <h2>1. Cadastro de Pacientes</h2>

          <div className="bg-white border rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <UserRound className="text-indigo-600" size={24} />
              <h3 className="m-0 text-xl font-bold">
                Dados cadastrais
              </h3>
            </div>

            <p>
              O sistema permite cadastrar informações completas do paciente:
            </p>

            <ul>
              <li>nome;</li>
              <li>CPF;</li>
              <li>telefone;</li>
              <li>e-mail;</li>
              <li>endereço;</li>
              <li>responsável legal;</li>
              <li>contato de emergência.</li>
            </ul>

          </div>

          <h2>2. Navegação do Prontuário</h2>

          <p>
            O prontuário é dividido em abas para facilitar a organização clínica.
          </p>

          <ul>
            <li>Visão Geral;</li>
            <li>Odontograma;</li>
            <li>Tratamentos;</li>
            <li>Evolução Clínica;</li>
            <li>Agenda;</li>
            <li>Imagens;</li>
            <li>Documentos;</li>
            <li>Anamnese;</li>
            <li>Financeiro;</li>
            <li>Histórico da IA.</li>
          </ul>

          <h2>3. Anamnese Digital</h2>

          <div className="bg-blue-50 border border-blue-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <FileText className="text-blue-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-blue-900">
                Histórico clínico inicial
              </h3>
            </div>

            <p className="text-blue-900">
              Utilize formulários digitais personalizados para registrar o histórico do paciente.
            </p>

            <p className="text-blue-900">
              Após o preenchimento, use <strong>Revisar com IA</strong> para organizar as respostas e identificar pontos que precisam de confirmação, sem alterar a anamnese automaticamente.
            </p>

          </div>

          <h2>4. Odontograma</h2>

          <div className="bg-white border rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <Smile className="text-pink-600" size={24} />
              <h3 className="m-0 text-xl font-bold">
                Controle odontológico visual
              </h3>
            </div>

            <p>
              O odontograma permite registrar:
            </p>

            <ul>
              <li>cáries;</li>
              <li>restaurações;</li>
              <li>fraturas;</li>
              <li>procedimentos;</li>
              <li>planejamentos;</li>
              <li>anotações clínicas.</li>
            </ul>

            <p>
              A ação <strong>Revisar marcações</strong> organiza somente os dados estruturados. Não analisa radiografias ou fotos, não diagnostica e não modifica o odontograma.
            </p>

          </div>

          <h2>5. Tratamentos</h2>

          <div className="bg-green-50 border border-green-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <Stethoscope className="text-green-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-green-900">
                Plano de tratamento
              </h3>
            </div>

            <p className="text-green-900">
              Registre procedimentos, diagnósticos, prioridades e planejamento clínico.
            </p>

          </div>

          <h2>6. Evolução Clínica</h2>

          <div className="bg-orange-50 border border-orange-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <Activity className="text-orange-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-orange-900">
                Histórico cronológico
              </h3>
            </div>

            <p className="text-orange-900">
              Registre observações clínicas, evolução dos tratamentos e informações dos atendimentos.
            </p>

            <p className="text-orange-900">
              Em cada evolução, use <strong>Revisar registro</strong> para conferir clareza e completude documental. A IA apenas aponta informações que podem precisar de confirmação.
            </p>

          </div>

          <h2>7. Agenda Integrada</h2>

          <div className="bg-indigo-50 border border-indigo-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <Calendar className="text-indigo-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-indigo-900">
                Consultas vinculadas
              </h3>
            </div>

            <p className="text-indigo-900">
              Os agendamentos ficam integrados diretamente ao prontuário do paciente.
            </p>

          </div>

          <h2>8. Imagens e Exames</h2>

          <div className="bg-white border rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <ImageIcon className="text-slate-700" size={24} />
              <h3 className="m-0 text-xl font-bold">
                Arquivos clínicos
              </h3>
            </div>

            <p>
              Armazene fotos clínicas, exames, documentos PDF e arquivos STL diretamente no prontuário.
            </p>

          </div>

          <h2>9. Documentos</h2>

          <div className="bg-purple-50 border border-purple-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <FileSignature className="text-purple-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-purple-900">
                Documentação clínica
              </h3>
            </div>

            <p className="text-purple-900">
              Gere contratos, receituários, atestados e termos de consentimento integrados ao paciente.
            </p>

            <p className="text-purple-900">
              Use <strong>Conferir documento</strong> para verificar o preenchimento antes da emissão. O histórico recente desta aba é filtrado pelo paciente aberto.
            </p>

          </div>

          <h2>10. Financeiro</h2>

          <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="text-emerald-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-emerald-900">
                Controle financeiro
              </h3>
            </div>

            <p className="text-emerald-900">
              Controle cobranças, pagamentos e faturamentos relacionados ao paciente.
            </p>

            <p className="text-emerald-900">
              Use <strong>Gerar resumo</strong> para obter uma fotografia operacional dos lançamentos, sem previsões ou alterações automáticas.
            </p>

          </div>

          <h2>11. IA Contextual e Histórico</h2>

          <div className="bg-cyan-50 border border-cyan-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <Brain className="text-cyan-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-cyan-900">
                Inteligência artificial no fluxo do prontuário
              </h3>
            </div>

            <p className="text-cyan-900">
              Na aba <strong>Visão Geral</strong>, use <strong>Gerar resumo</strong> para organizar alertas, anamnese, evoluções recentes, odontograma estruturado, tratamentos e documentos. Cada bloco informa sua fonte e nenhuma imagem é analisada.
            </p>

            <p className="text-cyan-900">
              A aba <strong>Histórico da IA</strong> reúne as revisões geradas em cada contexto, com origem, data e status. Ela é somente para consulta e não cria novas análises.
            </p>

          </div>

          <h2>12. Acesso em equipes com vários dentistas</h2>

          <div className="bg-purple-50 border border-purple-200 rounded-3xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="text-purple-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-purple-900">Acesso em equipes com vários dentistas</h3>
            </div>
            <p className="text-purple-900">
              O administrador pode permitir que um dentista veja todos os pacientes da clínica ou somente os pacientes atribuídos a ele. Um paciente também pode ter um dentista principal e acesso compartilhado com outros dentistas, quando o tratamento envolver mais de uma especialidade.
            </p>
            <p className="text-purple-900 mb-0">
              As abas, botões e recursos exibidos dependem das permissões do perfil. O escopo dos pacientes é independente da visualização da Agenda e pode ser ajustado em <strong>Usuários e permissões</strong>.
            </p>
          </div>

          <h2>13. Pacientes inativos e prontuários arquivados</h2>
          <p>Quando um paciente mudar de cidade ou encerrar o acompanhamento, use <strong>Inativar</strong> na lista de pacientes ou no cabeçalho do prontuário. Confirme a ação e, se desejar, informe um motivo.</p>
          <p>O paciente deixa de aparecer nas buscas padrão, no follow-up e nos lembretes gerais. O prontuário, documentos, imagens, atendimentos e registros financeiros permanecem preservados. Agendamentos existentes não são cancelados automaticamente.</p>
          <p>Para consultar o arquivo, altere o filtro <strong>Situação</strong> para <strong>Inativos</strong> ou <strong>Todos</strong>. Abra o prontuário normalmente. Se o paciente retornar, clique em <strong>Reativar</strong>: ele voltará às listas e poderá receber novos atendimentos.</p>
          <p>Essas ações dependem da permissão de inativar pacientes e ficam registradas na auditoria. Cadastros já mesclados não entram nessa lista; use o prontuário de destino.</p>
          <h2>14. Recomendações</h2>

          <div className="bg-green-50 border border-green-200 rounded-3xl p-6">

            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="text-green-700" size={24} />
              <h3 className="m-0 text-xl font-bold text-green-900">
                Boas práticas
              </h3>
            </div>

            <ul className="text-green-900">
              <li>Mantenha os dados atualizados;</li>
              <li>Registre evoluções após atendimentos;</li>
              <li>Atualize o odontograma regularmente;</li>
              <li>Anexe documentos importantes;</li>
              <li>Utilize a anamnese digital;</li>
              <li>Mantenha o prontuário organizado.</li>
            </ul>

          </div>

        </article>

      </main>

    </div>
  );
}
